import { useState } from 'react';
import { Button, Chip, Group, Modal, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useSnoozeStall, type Stall } from '@/api/ops';

const PRESETS = ['tomorrow', 'days3', 'week'] as const;
type Preset = (typeof PRESETS)[number];

/** A preset as a moment: the next morning at 8:00, or 3 / 7 days from now at 8:00. */
export function presetUntil(preset: Preset, now = new Date()): Date {
  const days = preset === 'tomorrow' ? 1 : preset === 'days3' ? 3 : 7;
  const d = new Date(now);
  d.setDate(d.getDate() + days);
  d.setHours(8, 0, 0, 0);
  return d;
}

/** `datetime-local` wants local wall time without a zone. */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Snooze one stall until a moment, with a note that is kept as an internal comment on its subject. */
export function SnoozeStallModal({ stall, onClose }: { stall: Stall | null; onClose: () => void }) {
  const { t } = useTranslation('ops');
  const snooze = useSnoozeStall();
  const [until, setUntil] = useState(() => toLocalInput(presetUntil('tomorrow')));
  const [note, setNote] = useState('');
  const [past, setPast] = useState(false);
  const parsed = new Date(until);
  const valid = !Number.isNaN(parsed.getTime());

  const close = () => {
    snooze.reset();
    setPast(false);
    setNote('');
    setUntil(toLocalInput(presetUntil('tomorrow')));
    onClose();
  };
  const submit = () => {
    if (!stall || !valid) return;
    if (parsed.getTime() <= Date.now()) {
      setPast(true);
      return;
    }
    snooze.mutate({ kind: stall.kind, subject_id: stall.subject_id, until: parsed.toISOString(), note }, { onSuccess: close });
  };

  return (
    <Modal opened={stall !== null} onClose={close} title={t('stalls.snooze.title')}>
      <Stack gap="sm">
        <Group gap="xs">
          {PRESETS.map((p) => (
            <Chip key={p} size="xs" checked={until === toLocalInput(presetUntil(p))} onChange={() => {
              setUntil(toLocalInput(presetUntil(p)));
              setPast(false);
            }}>
              {t(`stalls.snooze.preset.${p}`)}
            </Chip>
          ))}
        </Group>
        <TextInput
          type="datetime-local"
          label={t('stalls.snooze.until')}
          value={until}
          onChange={(e) => {
            setUntil(e.currentTarget.value);
            setPast(false);
          }}
          error={!valid || past ? t('stalls.snooze.future') : undefined}
        />
        <Textarea label={t('stalls.snooze.note')} description={t('stalls.snooze.noteHint')} value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={2} maxLength={4000} />
        {snooze.isError && (
          <Text fz={13} c="red">
            {errorMessage(snooze.error)}
          </Text>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={close}>
            {t('stalls.snooze.cancel')}
          </Button>
          <Button onClick={submit} disabled={!valid} loading={snooze.isPending}>
            {t('stalls.snooze.submit')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
